import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Calculator, Percent, PiggyBank, ClipboardCheck, Podcast, CalendarDays, Quote } from "lucide-react";
import { Hero } from "../components/Hero";
import { Marquee } from "../components/Marquee";
import { DiscoveryBar } from "../components/DiscoveryBar";
import { PropertyCard } from "../components/PropertyCard";
import { EnquiryForm } from "../components/EnquiryForm";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { getListings, getInsights, getTestimonials } from "../lib/api";
import { scrollToHash } from "../lib/lenis";
import { CONTACT, FRAMEWORK_STEPS, BUYER_POINTS, SELLER_POINTS, HERO_STATS, PODCAST_URL, PODCAST_EPISODES } from "../lib/site";
import { VideoCard } from "../components/VideoCard";
import assets from "../lib/design_assets.json";

const IMG = assets.media.image_urls;

const TOOL_CARDS = [
  { icon: Calculator, title: "Mortgage Repayment", text: "Estimate monthly repayments across loan amounts, rates and tenures.", tab: "mortgage" },
  { icon: Percent, title: "Stamp Duty Estimate", text: "BSD & ABSD breakdowns with effective dates and official IRAS sources.", tab: "stamp-duty" },
  { icon: PiggyBank, title: "Affordability Planning", text: "A disciplined first pass at budget, downpayment and borrowing power.", tab: "affordability" },
  { icon: ClipboardCheck, title: "Home Valuation Request", text: "A real valuation needs professional review — request one directly.", tab: "valuation" },
];

const outlineBtn =
  "group flex items-center gap-2 border border-ivory/40 px-6 py-3 text-[0.75rem] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-ivory hover:text-charcoal";

export default function Home() {
  const { hash } = useLocation();
  const [featured, setFeatured] = useState([]);
  const [insights, setInsights] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => scrollToHash(hash), 120);
      return () => clearTimeout(t);
    }
  }, [hash]);

  useEffect(() => {
    getListings({ featured: true, limit: 6 }).then((r) => setFeatured(r.results)).catch(() => {});
    getInsights().then((r) => setInsights(r.results.slice(0, 3))).catch(() => {});
    getTestimonials().then((r) => setTestimonials(r.results)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />
      <DiscoveryBar />
      <Marquee />

      {/* Featured properties */}
      <section className="py-24 lg:py-32" data-testid="featured-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading
              eyebrow="The Collection"
              title={<>Featured landed residences,<br />curated this season.</>}
            />
            <Reveal delay={0.15}>
              <Link to="/properties" data-testid="featured-view-all" className={outlineBtn}>
                View all residences
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <Stagger className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {featured.map((l) => (
              <StaggerItem key={l.id}>
                <PropertyCard listing={l} />
              </StaggerItem>
            ))}
          </Stagger>
          <p className="mt-8 text-xs text-ivory/40" data-testid="featured-sample-note">
            All cards above are clearly-labelled sample records demonstrating the listings experience.
          </p>
        </div>
      </section>

      {/* Meet Andy */}
      <section id="about" className="scroll-mt-24 border-y border-ivory/10 bg-[#181818] py-24 lg:py-32" data-testid="about-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <Reveal className="relative lg:col-span-5">
              <div className="absolute -left-4 -top-4 h-full w-full border border-ivory/30" aria-hidden="true" />
              <img
                src="/andy-family.png"
                alt="Andy Cheng with his family"
                loading="lazy"
                className="relative aspect-square w-full object-cover object-center"
                data-testid="about-portrait"
              />
              <div className="absolute bottom-6 left-6 bg-charcoal/85 px-5 py-3 backdrop-blur-sm">
                <p className="font-serif text-xl text-ivory">Andy Cheng</p>
                <p className="text-[0.65rem] uppercase tracking-[0.25em] text-ivory/70">Founder · PNG Landed Excellence</p>
              </div>
            </Reveal>

            <div className="lg:col-span-7 lg:pl-8 lg:pt-16">
              <SectionHeading
                eyebrow="Meet Andy"
                title={<>The landed specialist<br />behind the name.</>}
              />
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-ivory/70">
                  With more than 20 years in Singapore real estate, Andy Cheng leads PNG Landed Excellence —
                  a premier landed curation and marketing team within PropNex Realty, the country's largest
                  listed agency. As a Senior Associate Group District Director, he is recognised for disciplined
                  marketing systems, deep landed enclave knowledge across all 24 landed districts, and a mentorship
                  record that has grown a team of specialist landed curators.
                </p>
              </Reveal>
              <Stagger className="mt-10 grid grid-cols-1 gap-px border border-ivory/10 bg-ivory/10 sm:grid-cols-3">
                {HERO_STATS.map((s) => (
                  <StaggerItem key={s.label} className="bg-[#242424] px-6 py-8">
                    <p className="font-serif text-4xl text-ivory" data-testid={`stat-${s.label.slice(0, 8)}`}>{s.value}</p>
                    <p className="mt-2 text-[0.7rem] uppercase tracking-[0.2em] text-ivory/50">{s.label}</p>
                  </StaggerItem>
                ))}
              </Stagger>
              <Reveal delay={0.15}>
                <p className="mt-6 text-xs text-ivory/40">
                  CEA Reg. No. {CONTACT.ceaReg} · {CONTACT.agency}
                </p>
              </Reveal>
            </div>
          </div>

          <div className="mt-16 border-t border-ivory/10 pt-12" data-testid="about-podcast">
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="eyebrow flex items-center gap-2 text-ivory/60"><Podcast className="h-3.5 w-3.5" /> The Podcast</p>
                  <h3 className="mt-3 font-serif text-3xl text-ivory" data-testid="podcast-heading">Property Insights with PNG Landed Excellence</h3>
                  <p className="mt-2 max-w-xl text-sm text-ivory/60">Andy on landed strategy — watch the latest episodes right here.</p>
                </div>
                <a href={PODCAST_URL} target="_blank" rel="noopener noreferrer" data-testid="podcast-all-link" className="group inline-flex items-center gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-ivory/80 transition-colors hover:text-ivory">
                  All episodes
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </Reveal>
            <Stagger className="mt-8 grid gap-6 md:grid-cols-3">
              {PODCAST_EPISODES.map((v) => (
                <StaggerItem key={v.id}>
                  <VideoCard video={v} />
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* Framework */}
      <section className="grain relative bg-charcoal py-24 text-ivory lg:py-32" data-testid="framework-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Landed Excellence Framework"
            title={<>A disciplined path from<br />ambition to address.</>}
            description="The curated process PNG Landed Excellence uses to help buyers identify, shortlist and secure landed homes — and to position sellers for premium outcomes."
          />
          <Stagger className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {FRAMEWORK_STEPS.map((step) => (
              <StaggerItem key={step.no} className="group" >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={IMG[step.imageKey].url}
                    alt={step.title}
                    loading="lazy"
                    className="h-full w-full object-cover opacity-80 transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/20 to-transparent" />
                  <span className="absolute left-5 top-4 font-serif text-5xl italic text-ivory/50">{step.no}</span>
                </div>
                <h3 className="mt-5 font-serif text-2xl text-ivory">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ivory/60">{step.text}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="scroll-mt-24 py-24 lg:py-32" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            eyebrow="Why Work With Us"
            title={<>One team. Both sides<br />of the landed table.</>}
          />
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {[
              { title: "For Buyers", points: BUYER_POINTS, cta: "Start a buyer brief" },
              { title: "For Sellers", points: SELLER_POINTS, cta: "Request a sale strategy" },
            ].map((col, i) => (
              <Reveal key={col.title} delay={i * 0.12} className="border border-ivory/10 bg-[#242424] p-10">
                <p className="eyebrow text-ivory/50">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-4 font-serif text-3xl text-ivory">{col.title}</h3>
                <ul className="mt-6 space-y-4">
                  {col.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-ivory/70">
                      <span className="mt-[0.45rem] h-1.5 w-1.5 shrink-0 rotate-45 bg-ivory/40" aria-hidden="true" />
                      {p}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => scrollToHash("#contact")}
                  data-testid={`services-cta-${i}`}
                  className="group mt-8 inline-flex items-center gap-2 text-[0.75rem] font-semibold uppercase tracking-[0.2em] text-ivory"
                >
                  {col.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tools preview */}
      <section className="border-y border-ivory/10 bg-[#181818] py-24 lg:py-32" data-testid="tools-preview-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading
              eyebrow="Financial Tools"
              title={<>Numbers first.<br />Then keys.</>}
              description="Working demonstration calculators with honest disclaimers — built to be recreated in Wix."
            />
            <Reveal delay={0.15}>
              <Link to="/tools" data-testid="tools-preview-open" className={outlineBtn}>
                Open all tools
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Stagger className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {TOOL_CARDS.map((t) => (
              <StaggerItem key={t.tab}>
                <Link
                  to={`/tools?tab=${t.tab}`}
                  data-testid={`tool-card-${t.tab}`}
                  className="group flex h-full flex-col border border-ivory/10 bg-[#242424] p-8 transition-shadow duration-500 hover:shadow-[0_20px_40px_rgba(0,0,0,0.45)]"
                >
                  <t.icon className="h-7 w-7 text-ivory" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-2xl text-ivory">{t.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-ivory/60">{t.text}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-ivory/80 transition-colors group-hover:text-ivory">
                    Try it <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Insights preview */}
      <section className="py-24 lg:py-32" data-testid="insights-preview-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading
              eyebrow="Market Insights"
              title={<>Landed intelligence,<br />written plainly.</>}
            />
            <Reveal delay={0.15}>
              <Link to="/insights" data-testid="insights-view-all" className={outlineBtn}>
                All insights
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Stagger className="mt-14 grid gap-8 md:grid-cols-3">
            {insights.map((a) => (
              <StaggerItem key={a.slug}>
                <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group block">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                    <span className="absolute left-4 top-4 border border-ivory/20 bg-charcoal/80 px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-ivory backdrop-blur-sm">
                      {a.category}
                    </span>
                  </div>
                  <h3 className="mt-5 font-serif text-2xl leading-snug text-ivory transition-colors duration-300 group-hover:text-ivory/80">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/60">{a.excerpt}</p>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Voices: testimonial + podcast + events */}
      <section className="grain relative border-y border-ivory/10 bg-[#141414] py-24 text-ivory lg:py-32" data-testid="voices-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading eyebrow="Voices & Venues" title={<>Trusted in person.<br />Heard every week.</>} />
          <div className="mt-14 grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-6" data-testid="testimonial-block">
              <Quote className="h-9 w-9 text-ivory/60" strokeWidth={1.25} />
              {testimonials[0] && (
                <figure className="mt-6">
                  <blockquote className="font-serif text-2xl italic leading-relaxed text-ivory/90">
                    "{testimonials[0].quote}"
                  </blockquote>
                  <figcaption className="mt-6">
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-ivory">{testimonials[0].author}</p>
                    <p className="mt-1 text-xs text-ivory/50">{testimonials[0].context}</p>
                    <p className="mt-3 text-[0.7rem] text-ivory/40">{testimonials[0].source}</p>
                  </figcaption>
                </figure>
              )}
            </Reveal>
            <div className="flex flex-col justify-center lg:col-span-6">
              <Reveal delay={0.18}>
                <div className="group flex items-stretch border border-ivory/15" data-testid="events-card">
                  <div className="w-32 shrink-0 overflow-hidden sm:w-44">
                    <img src={IMG.event_1.url} alt="Landed Exhibition seminar" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col justify-center p-6 sm:p-8">
                    <p className="eyebrow flex items-center gap-2 text-ivory/70"><CalendarDays className="h-3.5 w-3.5" /> Seminars & Events</p>
                    <h3 className="mt-3 font-serif text-2xl text-ivory">The Landed Exhibition & Seminar Series</h3>
                    <p className="mt-2 text-sm text-ivory/60">Regular consumer seminars and an annual landed showcase. Enquire below for the next edition.</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="scroll-mt-24 py-24 lg:py-32" data-testid="contact-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Speak to Andy"
                title={<>Your landed chapter<br />starts with a call.</>}
                description="A non-obligatory conversation about your goals — buying, selling or simply understanding what your landed home could command."
              />
              <Reveal delay={0.1} className="mt-10 space-y-4">
                <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="block border border-ivory/10 bg-[#242424] px-6 py-5 transition-shadow hover:shadow-[0_20px_40px_rgba(0,0,0,0.45)]">
                  <p className="eyebrow text-ivory/50">Call</p>
                  <p className="mt-1 font-serif text-2xl text-ivory">{CONTACT.phoneDisplay}</p>
                </a>
                <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="block border border-ivory/10 bg-[#242424] px-6 py-5 transition-shadow hover:shadow-[0_20px_40px_rgba(0,0,0,0.45)]">
                  <p className="eyebrow text-ivory/50">WhatsApp</p>
                  <p className="mt-1 font-serif text-2xl text-ivory">Message Andy directly</p>
                </a>
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="block border border-ivory/10 bg-[#242424] px-6 py-5 transition-shadow hover:shadow-[0_20px_40px_rgba(0,0,0,0.45)]">
                  <p className="eyebrow text-ivory/50">Email</p>
                  <p className="mt-1 font-serif text-2xl text-ivory">{CONTACT.email}</p>
                </a>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="lg:col-span-7">
              <div className="border border-ivory/10 bg-[#242424] p-8 sm:p-12">
                <EnquiryForm kind="general" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
